import { Injectable, inject } from '@angular/core';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import type { BackendErrorResponse } from '@freelancer/freelancer-http';
import { isImageFile } from '@freelancer/ui';
import type { Observable } from 'rxjs';
import { BehaviorSubject, merge, of, fromEvent, firstValueFrom } from 'rxjs';
import { shareReplay, map, scan, last } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { configs } from './file-storage-backend.config';
import type {
  FullMetadata,
  SettableMetadata,
  StorageBucket,
  UploadTaskEvent,
} from './file-storage.model';
import type { UploadTask } from './file-storage.service';
import { FileStorage } from './file-storage.service';

export interface FileAttachment {
  id: string;
  file: File;
  // undefined value is for non-image files
  filePreviewUrl$: Observable<string | undefined>;
  metadata?: SettableMetadata;
}

export interface FileUpload extends FileAttachment {
  uploadTask: UploadTask;
  uploadTaskEvent$: Observable<UploadTaskEvent>;
  uploadPercentage$: Observable<number>;
  persistFailed: boolean;
  isLegacyUpload: boolean;
}

export type NewFileServiceFlowCallback = (
  fileUpload: FileUpload,
  metadata: FullMetadata,
) => void;

/**
 * The class for managing file attachment and file uploads
 * using the FileStorage service as the backend upload service.
 */
export class FileUploadSession {
  private fileUploads$: Observable<readonly FileUpload[]>;
  private addFileUploadSubject$ = new BehaviorSubject<FileUpload | undefined>(
    undefined,
  );
  private addFileUpload$ = this.addFileUploadSubject$
    .asObservable()
    .pipe(
      map(
        fileUpload => (fileUploads: FileUpload[]) =>
          fileUpload
            ? this.add<FileUpload>(fileUpload, fileUploads)
            : fileUploads,
      ),
    );

  private removeFileUploadSubject$ = new BehaviorSubject<
    FileUpload | undefined
  >(undefined);
  private removeFileUpload$ = this.removeFileUploadSubject$
    .asObservable()
    .pipe(
      map(
        fileUpload => (fileUploads: FileUpload[]) =>
          fileUpload
            ? this.remove<FileUpload>(fileUpload.id, fileUploads)
            : fileUploads,
      ),
    );

  private updateFileUploadSubject$ = new BehaviorSubject<
    FileUpload | undefined
  >(undefined);
  private updateFileUpload$ = this.updateFileUploadSubject$
    .asObservable()
    .pipe(
      map(
        fileUploadUpdate => (fileUploads: FileUpload[]) =>
          fileUploadUpdate
            ? this.update<FileUpload>(fileUploadUpdate, fileUploads)
            : fileUploads,
      ),
    );

  /**
   * File attachment related observables and actions.
   * This reactively build the file attachments list based on the different
   * action (e.g. add or remove) that the users did.
   */
  private fileAttachments$: Observable<readonly FileAttachment[]>;
  private addFileAttachmentSubject$ = new BehaviorSubject<
    FileAttachment | undefined
  >(undefined);
  private addFileAttachment$ = this.addFileAttachmentSubject$
    .asObservable()
    .pipe(
      map(fileAttachment => (fileAttachments: FileAttachment[]) => {
        if (
          fileAttachment &&
          !this.isFileExists(fileAttachment.file, fileAttachments)
        ) {
          const fixedFilenameFile = this.fixConflictFilenameFile(
            fileAttachment.file,
            fileAttachments,
          );
          const fixedFileAttachment = {
            ...fileAttachment,
            file: fixedFilenameFile,
          };

          return this.add<FileAttachment>(fixedFileAttachment, fileAttachments);
        }

        return fileAttachments;
      }),
    );
  private removeFileAttachmentSubject$ = new BehaviorSubject<
    FileAttachment | undefined
  >(undefined);
  private removeFileAttachment$ = this.removeFileAttachmentSubject$
    .asObservable()
    .pipe(
      map(
        fileAttachment => (fileAttachments: FileAttachment[]) =>
          fileAttachment
            ? this.remove<FileAttachment>(fileAttachment.id, fileAttachments)
            : fileAttachments,
      ),
    );

  private updateFileAttachmentSubject$ = new BehaviorSubject<
    FileAttachment | undefined
  >(undefined);
  private updateFileAttachment$ = this.updateFileAttachmentSubject$
    .asObservable()
    .pipe(
      map(
        fileUpdate => (fileAttachments: FileAttachment[]) =>
          fileUpdate
            ? this.update<FileAttachment>(fileUpdate, fileAttachments)
            : fileAttachments,
      ),
    );

  private isLegacyUpload = false;

  constructor(
    private fileStorage: FileStorage,
    private bucket: StorageBucket,
  ) {
    this.fileAttachments$ = merge(
      this.addFileAttachment$,
      this.removeFileAttachment$,
      this.updateFileAttachment$,
    ).pipe(
      scan(
        (accumulator, fileAttachmentOperation) =>
          fileAttachmentOperation(accumulator),
        [] as FileAttachment[],
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.fileUploads$ = merge(
      this.addFileUpload$,
      this.removeFileUpload$,
      this.updateFileUpload$,
    ).pipe(
      scan(
        (accumulator, fileUploadOperation) => fileUploadOperation(accumulator),
        [] as FileUpload[],
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.isLegacyUpload = this.isBucketUsingLegacyUpload(bucket);
  }

  /**
   * This function adds the file in the list of file attachments to be uploaded / sent.
   */
  addFile(file: File, metadata?: SettableMetadata): void {
    if (!file) {
      return;
    }

    // Create an observable of preview URL for image file
    // while empty URL for non-image file
    const filePreviewUrl$ = isImageFile(file.name)
      ? this.readFileAsDataURL(file).pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
        )
      : of(undefined);

    this.addFileAttachmentSubject$.next({
      id: uuidv4(),
      file,
      filePreviewUrl$,
      metadata,
    });
  }

  /**
   * This function removes the file in the list of file attachments.
   */
  removeFile(fileAttachment: FileAttachment): void {
    this.removeFileAttachmentSubject$.next(fileAttachment);
  }

  /**
   * This function removes the file in the file upload list given the id of the
   * uploading file. The function will first cancel any running uploading file
   * before removing it from the uploading file list.
   */
  async removeFileUpload(fileUpload: FileUpload): Promise<void> {
    // Make sure uploading file is cancelled before removing in the uploading list
    await firstValueFrom(fileUpload.uploadTaskEvent$).then(uploadTaskEvent => {
      if (uploadTaskEvent.state === 'running') {
        fileUpload.uploadTask.cancel();
      }
    });

    this.removeFileUploadSubject$.next(fileUpload);
  }

  /**
   * This function marks the file upload persist as failed and is only used in the
   * new file service flow. The new file service flow uploads the file temporarily which
   * needs to be marked as persist in the backend.
   */
  markFileUploadPersistAsFailed(
    fileUpload: FileUpload,
    error: BackendErrorResponse<BackendAllErrorCodes>,
  ): void {
    const updatedFileUpload = {
      ...fileUpload,
      ...{
        uploadTaskEvent$: fileUpload.uploadTaskEvent$.pipe(
          map(event => {
            return {
              ...event,
              ...{
                state: error.status,
                error: {
                  ...error,
                },
              },
            } as UploadTaskEvent;
          }),
        ),
      },
      persistFailed: true,
    };

    this.updateFileUpload(updatedFileUpload);
  }

  /**
   * Returns an observable of list of file attachments that are ready to be uploaded.
   */
  getFileAttachments(): Observable<readonly FileAttachment[]> {
    return this.fileAttachments$;
  }

  /**
   * Returns an observable of list of uploading files.
   */
  getFileUploads(): Observable<readonly FileUpload[]> {
    return this.fileUploads$;
  }

  /**
   * This function updates the metadata of the passed file attachment.
   */
  updateFileAttachmentMetadata(
    fileAttachment: FileAttachment,
    metadata: SettableMetadata,
  ): void {
    const updatedFileAttachment = {
      ...fileAttachment,
      metadata,
    };

    this.updateFileAttachment(updatedFileAttachment);
  }

  /**
   * Clear all the files in the list of file attachments.
   */
  clearAllFileAttachments(): void {
    firstValueFrom(this.fileAttachments$).then(fileAttachments => {
      fileAttachments.forEach(fileAttachment => {
        this.removeFileAttachmentSubject$.next(fileAttachment);
      });
    });
  }

  /**
   * This function retries the given file upload.
   * If the file storage bucket is using the new file service flow,
   * it will require the callback function responsible for making the
   * uploaded file to persist, else the legacy flow does not require
   * callback function since the uploaded file is already persisting.
   */
  async retryFileUpload(
    fileUpload: FileUpload,
    newFileServiceFlowCallback?: NewFileServiceFlowCallback,
  ): Promise<void> {
    if (!this.isLegacyUpload && !newFileServiceFlowCallback) {
      throw new Error(
        'Callback function is required for uploading file in new file service flow',
      );
    }

    if (this.isLegacyUpload && !!newFileServiceFlowCallback) {
      throw new Error(
        'Callback function should only be passed for new file service flow',
      );
    }

    // Make sure that the uploading of file is already cancelled
    await firstValueFrom(fileUpload.uploadTaskEvent$).then(uploadTaskEvent => {
      if (uploadTaskEvent.state === 'running') {
        fileUpload.uploadTask.cancel();
      }
    });

    const uploadTask = this.fileStorage.upload(
      this.bucket,
      fileUpload.file,
      fileUpload.metadata,
    );

    // Only update the file upload to retain the order and id
    const updatedFileUpload = {
      ...fileUpload,
      uploadTask,
      uploadTaskEvent$: uploadTask.changes(),
      uploadPercentage$: uploadTask.percentageChanges(),
      persistFailed: false,
      isLegacyUpload: this.isLegacyUpload,
    };

    this.updateFileUpload(updatedFileUpload);

    if (!this.isLegacyUpload && !!newFileServiceFlowCallback) {
      this.processNewFileServiceFlow(
        updatedFileUpload,
        newFileServiceFlowCallback,
      );
    }
  }

  /**
   * This function will trigger the uploading of all the files in the list of file attachments,
   * and move each uploading files to the list of uploading files. The callback is only used for
   * the new file service flow, the parameter should not be passed when using the legacy flow.
   */
  send(newFileServiceFlowCallback?: NewFileServiceFlowCallback): void {
    if (!this.isLegacyUpload && !newFileServiceFlowCallback) {
      throw new Error(
        'Callback function is required for uploading file in new file service flow',
      );
    }

    if (this.isLegacyUpload && !!newFileServiceFlowCallback) {
      throw new Error(
        'Callback function should only be passed for new file service flow',
      );
    }

    firstValueFrom(this.fileAttachments$).then(fileAttachments => {
      fileAttachments.forEach(fileAttachment => {
        this.uploadFileAttachment(fileAttachment, newFileServiceFlowCallback);
      });
    });
  }

  /**
   * NOTE: THIS SHOULD ONLY BE USED WHEN MAKING THE CURRENT BUCKET TO FALLBACK TO THE LEGACY FLOW
   * This function update the storage bucket to be used for the file upload session instance.
   */
  updateFileUploadSessionBucket(bucket: StorageBucket): void {
    this.bucket = bucket;
    this.isLegacyUpload = this.isBucketUsingLegacyUpload(bucket);
  }

  private async uploadFileAttachment(
    fileAttachment: FileAttachment,
    newFileServiceFlowCallback?: NewFileServiceFlowCallback,
  ): Promise<void> {
    const uploadTask = this.fileStorage.upload(
      this.bucket,
      fileAttachment.file,
      fileAttachment.metadata,
    );

    const fileUpload = {
      ...fileAttachment,
      uploadTask,
      uploadTaskEvent$: uploadTask.changes(),
      uploadPercentage$: uploadTask.percentageChanges(),
      persistFailed: false,
      isLegacyUpload: this.isLegacyUpload,
    };

    this.addFileUploadSubject$.next(fileUpload);
    this.removeFileAttachmentSubject$.next(fileAttachment);

    if (!this.isLegacyUpload && !!newFileServiceFlowCallback) {
      this.processNewFileServiceFlow(fileUpload, newFileServiceFlowCallback);
    }
  }

  private async processNewFileServiceFlow(
    fileUpload: FileUpload,
    newFileServiceFlowCallback: NewFileServiceFlowCallback,
  ): Promise<void> {
    const taskEvent = await firstValueFrom(
      fileUpload.uploadTaskEvent$.pipe(last()),
    );

    if (taskEvent.state === 'success') {
      newFileServiceFlowCallback(fileUpload, taskEvent.metadata);
    }
  }

  /**
   * Read the file as data URL used for previewing of image files
   * while it is not yet uploaded.
   */
  private readFileAsDataURL(file: File): Observable<string> {
    const fileReader = new FileReader();

    fileReader.readAsDataURL(file);

    // We can cast to string here as we are using the `readAsDataURL` function,
    // the result will always be a string.
    return fromEvent(fileReader, 'load').pipe(
      map(() => fileReader.result as string),
    );
  }

  private add<T extends FileAttachment | FileUpload>(t: T, tList: T[]): T[] {
    return [...tList, t];
  }

  private remove<T extends FileAttachment | FileUpload>(
    tId: string,
    tList: T[],
  ): T[] {
    return tList.filter(t => t.id !== tId);
  }

  private update<T extends FileAttachment | FileUpload>(
    tNew: T,
    tList: T[],
  ): T[] {
    const fileIndex = tList.findIndex(t => t.id === tNew.id);

    if (fileIndex !== -1) {
      // Retain the positioning of the updated file in the list
      return [
        ...tList.slice(0, fileIndex),
        tNew,
        ...tList.slice(fileIndex + 1),
      ];
    }

    return tList;
  }

  /**
   * This function replaces the old file upload with the passed
   * updated file upload while maintaining the order of the file attachment list.
   */
  private updateFileUpload(updatedFileUpload: FileUpload): void {
    this.updateFileUploadSubject$.next(updatedFileUpload);
  }

  /**
   * This function replaces the old file attachment with the passed
   * updated file attachment while maintaining the order of the file attachment list.
   */
  private updateFileAttachment(updatedFileAttachment: FileAttachment): void {
    this.updateFileAttachmentSubject$.next(updatedFileAttachment);
  }

  /**
   * Migrated from file-upload-legacy.ts
   *
   * Check if the same file exists in the files array.
   */
  private isFileExists(file: File, fileAttachments: FileAttachment[]): boolean {
    return (
      !!fileAttachments &&
      fileAttachments.some(
        f => f.file.name === file.name && f.file.size === file.size,
      )
    );
  }

  /**
   * Migrated from file-upload-legacy.ts
   *
   * This function checks for files with conflicting filenames (e.g. 2 test.txt)
   * in the files array.
   *
   * This returns the original file if there is no conflicting name, else it
   * will return a renamed file (e.g. test.txt -> test_1.txt, test_2.txt, etc.)
   */
  private fixConflictFilenameFile(
    file: File,
    fileAttachments: FileAttachment[],
  ): File {
    const filename = file.name.split('.');
    const extensions = filename.slice(1).join('.');
    const matchingFiles = fileAttachments
      .filter(
        f =>
          f.file.name.startsWith(filename[0]) &&
          f.file.name.endsWith(extensions),
      )
      .sort((a, b) => (a.file.name < b.file.name ? -1 : 1));

    // only modify name if there's a match (also runs when there are no matchingFiles)
    if (matchingFiles.every(f => f.file.name !== file.name)) {
      return file;
    }

    const lastMatch = matchingFiles.at(-1);
    if (!lastMatch) {
      return file;
    }
    const matchName = lastMatch.file.name.split('.');
    const fileNumber =
      filename[0] !== matchName[0]
        ? Number(
            matchName[0].substring(
              matchName[0].length - 2,
              matchName[0].length - 2 + 2,
            ),
          ) + 1
        : 1;
    const fileNumberPad = `_${`0${fileNumber}`.slice(-2)}`;
    filename[0] += fileNumberPad;
    return new File([file.slice(0, file.size)], filename.join('.'));
  }

  /**
   * This function checks if the storage bucket is using legacy upload.
   */
  private isBucketUsingLegacyUpload(bucket: StorageBucket): boolean {
    // Random ID to simply get the bucket config
    const randomId = uuidv4();

    return configs[bucket]({
      bucket,
      id: randomId,
    }).isLegacy;
  }
}

/**
 * Factory class that creates a FileUploadSession for managing
 * file attachment and file uploads.
 */
@Injectable({ providedIn: 'root' })
export class FileUploadSessionFactory {
  private fileStorage = inject(FileStorage);

  /**
   * Create an instance of FileUploadSession for the passed storage bucket.
   */
  createSession(bucket: StorageBucket): FileUploadSession {
    return new FileUploadSession(this.fileStorage, bucket);
  }
}
